import React, { useRef, useState, useEffect } from "react";
import { m, useInView, useReducedMotion, useMotionValue, useSpring, useTransform } from "framer-motion";
import HeroDashboard from "./HeroDashboard";
import { useMarketingMotion } from "./marketing/MarketingMotion";

/* ────────────────────────────────────────────────────────────────────────────
   HeroScrollReveal — a restrained product-film reveal. Only roomy desktop viewports
   receive the pinned hand-off from hero copy to the governed product visual. Laptop,
   tablet, mobile, reduced-motion and the global motion-pause state all use ordinary
   document flow, preventing sticky traps and section overlap. The product images are
   never blurred, tilted or scaled; only the outer stage translates and fades.
   ──────────────────────────────────────────────────────────────────────────── */

const EASE = [0.23, 1, 0.32, 1];

// soft ambient glows + faint stars shared by both layouts
function Glows() {
  return (
    <>
      <div className="absolute left-1/2 -translate-x-1/2 top-[-14%] w-[78%] h-[42%] rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(37,99,235,0.30), rgba(59,130,246,0.10), transparent)", filter: "blur(54px)" }} aria-hidden="true" />
      <div className="absolute right-[-6%] top-[5%] w-[30%] h-[34%] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(249,115,22,0.14), transparent 70%)", filter: "blur(60px)" }} aria-hidden="true" />
      <div className="hero-stars absolute inset-0 pointer-events-none" aria-hidden="true" />
    </>
  );
}

export default function HeroScrollReveal({ children }) {
  const prefersReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const reduce = Boolean(prefersReducedMotion || paused);
  const [pinned, setPinned] = useState(false);
  const wrapRef = useRef(null);
  const mobileDashboardRef = useRef(null);
  const progress = useMotionValue(0);
  const [demoActive, setDemoActive] = useState(false);
  const mobileDashboardInView = useInView(mobileDashboardRef, { amount: 0.3, margin: "-10% 0px -10% 0px" });

  // Pin only when both width and height leave enough room for the complete story UI.
  useEffect(() => {
    const check = () => setPinned(
      typeof window !== "undefined"
      && window.innerWidth >= 1280
      && window.innerHeight >= 800
      && !reduce
    );
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [reduce]);

  // manual scroll progress across the pinned wrapper
  useEffect(() => {
    if (!pinned) { progress.set(0); return undefined; }
    const onScroll = () => {
      const el = wrapRef.current; if (!el) return;
      const rect = el.getBoundingClientRect();
      const dist = el.offsetHeight - window.innerHeight;
      progress.set(dist > 0 ? Math.min(1, Math.max(0, -rect.top / dist)) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, [pinned, progress]);

  const sp = useSpring(progress, { stiffness: 80, damping: 22, mass: 0.5 });
  // The story is "active" the moment its stage is fully opaque (dOpacity completes at 0.34) — the
  // old 0.48 threshold left a 0.34–0.48 band where the dashboard was completely visible yet the
  // story sat inert reporting "Waiting until visible" and Pause/Play appeared dead.
  useEffect(() => { const u = sp.on("change", (v) => setDemoActive(v > 0.34)); return () => u(); }, [sp]);

  // The stage settles early, leaving a long readable hold for the guided chapters.
  const dOpacity = useTransform(sp, [0.04, 0.34], [0, 1]);
  const dTransform = useTransform(sp, [0, 0.58], ["translate3d(0, 112px, 0)", "translate3d(0, 0px, 0)"]);
  // copy recedes as the dashboard takes over
  const cOpacity = useTransform(sp, [0, 0.3], [1, 0]);
  const cTransform = useTransform(sp, [0, 0.3], ["translate3d(0, 0px, 0)", "translate3d(0, -36px, 0)"]);
  // Once the copy has receded it must stop hit-testing: it keeps z-20 above the story stage
  // (z-10), so at opacity 0 it was an INVISIBLE click shield over the story's Pause/prev/next
  // header controls — the "buttons don't work" bug. Hand pointer events back only while the
  // copy is genuinely readable.
  const cPointerEvents = useTransform(sp, (v) => (v > 0.22 ? "none" : "auto"));
  // glowing chord brightens + swells slightly
  const chordOpacity = useTransform(sp, [0, 0.45], [0.55, 1]);
  const chordTransform = useTransform(sp, [0, 1], ["scaleX(1)", "scaleX(1.05)"]);
  const chordGlow = useTransform(sp, [0, 0.6], [0.35, 0.9]);

  // ── mobile / reduced-motion: plain stacked reveal ──
  if (!pinned) {
    return (
      <div id="dashboard-showcase" className="relative overflow-hidden">
        <Glows />
        <div className="hero-chord" style={{ opacity: 0.7 }} aria-hidden="true" />
        <div className="hero-chord-glow" style={{ opacity: 0.55 }} aria-hidden="true" />
        <div className="hero-wave" aria-hidden="true"><span /><span /><span /></div>
        <div className="relative z-20 px-5 pt-7 sm:pt-10 text-center">
          <div className="max-w-[1180px] mx-auto">{children}</div>
        </div>
        <div className="relative z-10 mt-8 px-4 pb-8 sm:mt-10">
          <m.div ref={mobileDashboardRef} id="dashboard-view" className="w-[min(1120px,94vw)] mx-auto scroll-mt-24"
            initial={reduce ? false : { opacity: 0, transform: "translate3d(0,32px,0)" }}
            whileInView={reduce ? undefined : { opacity: 1, transform: "translate3d(0,0,0)" }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, ease: EASE }}>
            <HeroDashboard active={mobileDashboardInView} />
          </m.div>
        </div>
      </div>
    );
  }

  // ── desktop: pinned cinematic reveal ──
  return (
    <div ref={wrapRef} id="dashboard-showcase" style={{ height: "180vh" }} className="relative">
      <div className="sticky top-0 h-screen overflow-hidden">
        <Glows />
        <m.div className="hero-chord" style={{ opacity: chordOpacity, transform: chordTransform }} aria-hidden="true" />
        <m.div className="hero-chord-glow" style={{ opacity: chordGlow }} aria-hidden="true" />
        <div className="hero-wave" aria-hidden="true"><span /><span /><span /></div>

        {/* copy — upper area, recedes on scroll and stops hit-testing as it fades */}
        <m.div className="absolute inset-x-0 top-0 z-20 px-5 pt-[78px] text-center" style={{ opacity: cOpacity, transform: cTransform, pointerEvents: cPointerEvents }}>
          <div className="max-w-[1180px] mx-auto">{children}</div>
        </m.div>

        {/* Product stage — rises from the chord and stays optically sharp. */}
        <div className="absolute inset-0 z-10 flex items-end justify-center px-4 pb-[3vh]">
          <m.div className="w-[min(1220px,93vw)]"
            style={{ height: "min(78vh, 760px)", opacity: dOpacity, transform: dTransform }}>
            <HeroDashboard active={demoActive} />
          </m.div>
        </div>
      </div>
    </div>
  );
}
